import { useCallback, useEffect, useRef, useState } from 'react';
import { Backpack, Plus, X } from 'lucide-react';
import type { Me } from '../api/auth';
import { packingApi } from '../api/packing';
import { ItemBadge, UnitOwnedToggle } from '../ui/ItemBadge';
import { gearApi } from '../api/gear';
import type { PackingList } from '../api/packing';
import { eventTypesApi } from '../api/eventTypes';
import type { EventType } from '../api/eventTypes';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { SelectField, TextField } from '../ui/Field';

/**
 * The lists a unit keeps, and the lists a person keeps — of what to bring.
 *
 * They sit on one screen and are emphatically **not** the same thing, which the screen says out
 * loud: the unit's are the unit's record, an officer writes them, and they stay behind when
 * somebody leaves. A person's own belong to them, follow them into every unit they are in, and no
 * leader can see or change one. That difference is the single most likely place in the model for a
 * query-filter mistake (§11), so it is worth being visible here rather than only in the schema.
 */
export function PackingListsScreen({
  unitId,
  unitName,
  canManage,
  subgroupLabelSingular,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canManage: boolean;
  /** The unit's own word for a group -- Patrol, Den, Crew. Rendered, never assumed (rule 16). */
  subgroupLabelSingular: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [unitLists, setUnitLists] = useState<PackingList[] | null>(null);
  const [kinds, setKinds] = useState<EventType[]>([]);
  const [myLists, setMyLists] = useState<PackingList[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [newName, setNewName] = useState('');
  const nameField = useRef<HTMLInputElement>(null);
  const [newLevel, setNewLevel] = useState('Personal');
  const [newOwner, setNewOwner] = useState<'unit' | 'me'>(canManage ? 'unit' : 'me');

  const load = useCallback(async () => {
    try {
      const [templates, mine] = await Promise.all([packingApi.templates(unitId), packingApi.mine()]);

      // The unit's own, separated from the person's by who owns them rather than by which call
      // returned them: the unit route hands back both, because both are things this person can
      // copy from here.
      setUnitLists(templates.filter((t) => t.ownerPersonId === null));
      setMyLists(mine);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the saved lists.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // The unit's kinds of event, so a saved list can be filed under one. Quiet on failure: it
    // removes a control rather than the screen.
    eventTypesApi.list(unitId).then(setKinds).catch(() => undefined);
  }, [unitId]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setFieldErrors({});

    try {
      await action();
      await load();
      return true;
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(e.errors);
      } else {
        setError(e instanceof Error ? e.message : 'That did not work.');
      }
      return false;
    }
  }

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-text-muted)', margin: 0 }}
            >
              <Backpack size={14} aria-hidden="true" />
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-2)' }}
            >
              Saved lists
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '46ch' }}
            >
              What to bring, saved so nobody writes it out again. The unit&rsquo;s lists go onto
              an event; yours stay yours. Putting one on an event copies it, so tidying a list here
              never changes something people have already got ready for.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            <form
              className="flex flex-col"
              style={{
                gap: 'var(--space-4)',
                background: 'var(--color-surface-raised)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-4)',
                marginBottom: 'var(--space-5)',
              }}
              onSubmit={async (e) => {
                e.preventDefault();

                // Not disabled, so there is no reason to render underneath it -- the same move
                // AddSomething made in TROOP-74. An empty field is its own explanation, and
                // clicking Save with nothing typed puts the cursor where the answer goes. The
                // design-system rule is untouched: it governs *disabled* controls.
                if (!newName.trim()) {
                  nameField.current?.focus();
                  return;
                }

                const saved = await run(() =>
                  newOwner === 'me'
                    ? packingApi.saveForMe(newName)
                    : packingApi.saveForUnit(unitId, { name: newName, level: newLevel }),
                );

                if (saved) setNewName('');
              }}
            >
              <TextField
                ref={nameField}
                label="Start a list"
                required
                value={newName}
                error={fieldErrors.name?.[0]}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Normal campout"
              />

              {canManage && (
                <SelectField
                  label="Whose"
                  value={newOwner}
                  onChange={(e) => setNewOwner(e.target.value as 'unit' | 'me')}
                >
                  <option value="unit">The unit&rsquo;s</option>
                  <option value="me">Mine</option>
                </SelectField>
              )}

              {newOwner === 'unit' && (
                <SelectField
                  label="Who brings it"
                  value={newLevel}
                  error={fieldErrors.level?.[0]}
                  onChange={(e) => setNewLevel(e.target.value)}
                >
                  <option value="Personal">Everyone brings their own</option>
                  <option value="Subgroup">
                    Each {subgroupLabelSingular.toLowerCase()} brings it
                  </option>
                  <option value="Unit">The unit brings it</option>
                </SelectField>
              )}

              <div>
                <Button variant="primary" type="submit">
                  <Plus size={16} aria-hidden="true" />
                  Save it
                </Button>
              </div>
            </form>

            <h2 className="t-label" style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}>
              {unitName}&rsquo;s lists
            </h2>

            {unitLists && unitLists.length === 0 ? (
              <EmptyState
                icon={Backpack}
                headline="No saved lists yet"
                explanation="Save the list your unit actually uses and it will be there the next time somebody adds an event."
              />
            ) : (
              <ListCards
                lists={unitLists ?? []}
                unitId={unitId}
                kinds={kinds}
                subgroupLabelSingular={subgroupLabelSingular}
                onRun={run}
              />
            )}

            <h2
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: 'var(--space-6) 0 var(--space-2)' }}
            >
              Yours
            </h2>
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3)' }}
            >
              These belong to you rather than to {unitName}. They follow you to every unit you are
              in, and nobody else can see them.
            </p>

            {myLists && myLists.length === 0 ? (
              <EmptyState
                icon={Backpack}
                headline="Nothing kept yet"
                explanation="Keep the list you actually used and next time starts from it rather than from a blank page."
              />
            ) : (
              // No kinds passed: your own lists are yours wherever you are, so they are not
              // filed under one unit's vocabulary. The control is absent rather than disabled,
              // because there is no permission you are waiting for.
              <ListCards
                lists={myLists ?? []}
                unitId={unitId}
                kinds={[]}
                subgroupLabelSingular={subgroupLabelSingular}
                onRun={run}
              />
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/**
 * The three levels as a reader would say them. See `EventPacking.levelWords` for the same idea on
 * the event side. The middle one renders the unit's own word rather than "group" (rule 16): a Pack
 * reading "the group brings it" one tap from a button that says Dens is the mismatch this fixes.
 */
function levelWords(level: string, subgroupLabelSingular: string): string {
  switch (level) {
    case 'Personal':
      return 'Everyone brings their own';
    case 'Subgroup':
      return `Each ${subgroupLabelSingular.toLowerCase()} brings it`;
    default:
      return 'The unit brings it';
  }
}

function ListCards({
  lists,
  unitId,
  kinds,
  subgroupLabelSingular,
  onRun,
}: {
  lists: PackingList[];
  unitId: string;
  /** The unit's kinds of event. Empty for a person's own lists, which belong to no unit. */
  kinds: EventType[];
  subgroupLabelSingular: string;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  return (
    <ul className="flex flex-col" style={{ gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
      {lists.map((list) => (
        <li key={list.id}>
          <ListCard
            list={list}
            unitId={unitId}
            kinds={kinds}
            subgroupLabelSingular={subgroupLabelSingular}
            onRun={onRun}
          />
        </li>
      ))}
    </ul>
  );
}

function ListCard({
  list,
  unitId,
  kinds,
  subgroupLabelSingular,
  onRun,
}: {
  list: PackingList;
  unitId: string;
  kinds: EventType[];
  subgroupLabelSingular: string;
  onRun: (action: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [adding, setAdding] = useState('');
  const addField = useRef<HTMLInputElement>(null);

  /*
    Who may mark a line as the troop's on THIS list.

    `canWrite` is already the server's per-row answer, and on a unit- or group-owned template
    PackingAccessPolicy grants Write to officers and to nobody else -- so it is the office, asked
    once, without a second copy of the rule on the client.

    The owner check is the other half and is not cosmetic: a person's own template is global, has
    no UnitId at all, and the route refuses to mark one. Offering the box there would be a control
    that always fails.
  */
  const canMarkGear = list.canWrite && list.ownerPersonId === null;

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
      }}
    >
      <span className="t-body-strong" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
        {list.name}
      </span>
      <span className="t-caption tabular" style={{ color: 'var(--color-text-muted)' }}>
        {levelWords(list.level, subgroupLabelSingular)} &middot;{' '}
        {list.items.length} {list.items.length === 1 ? 'thing' : 'things'}
      </span>

      {/* Which kind of event this is for. Filing it here is what makes it appear on every
          event of that kind, rather than something somebody has to find by name next March. */}
      {list.canWrite && kinds.length > 0 && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <SelectField
            label="Suggest it for"
            value={list.eventTypeId ?? ''}
            onChange={(e) => onRun(() => packingApi.suggestForType(unitId, list.id, e.target.value || null))}
          >
            <option value="">No particular kind of event</option>
            {kinds.map((kind) => (
              <option key={kind.id} value={kind.id}>
                {kind.name}
              </option>
            ))}
          </SelectField>
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
        {list.items.map((item) => (
          <li
            key={item.id}
            className="flex items-center justify-between gap-2"
            style={{ borderTop: '1px solid var(--color-border-subtle)' }}
          >
            {/* flex-1 rather than justify-between doing the work. With three items on the row --
                name, the troop's box, remove -- space-between puts the FREE space between them, so
                the checkbox lands at a different x on every row and a column of them reads as
                ragged (seen in prod, TROOP-168). Letting the name absorb the slack packs the two
                controls against the right edge at their own constant widths, which is what makes
                the boxes line up. minWidth: 0 so a long name wraps inside the row rather than
                pushing the controls off it. */}
            <span
              className="t-body"
              style={{
                color: 'var(--color-text-primary)',
                paddingBlock: 'var(--space-2)',
                flex: '1 1 8rem',
                // A floor rather than 0, matching EventPacking's row. This row is the lighter of
                // the two -- no assignee select -- so nothing here can currently squeeze the name
                // to nothing, but `minWidth: 0` is what let that happen there and the two rows
                // should not disagree about it.
                minWidth: '6rem',
              }}
            >
              {item.text}
              {item.category && (
                <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
                  {' '}
                  &middot; {item.category}
                </span>
              )}
              {!canMarkGear && item.isUnitOwned && (
                <>
                  {' '}
                  <ItemBadge />
                </>
              )}
            </span>

            {/* Marking the trailer HERE is the one that pays for itself: the flag rides
                CopiedInto, so every trip published from this list inherits it (TROOP-168). */}
            {canMarkGear && (
              <UnitOwnedToggle
                itemText={item.text}
                isUnitOwned={item.isUnitOwned}
                busy={false}
                onChange={(next) =>
                  onRun(() => gearApi.setUnitOwned(unitId, list.id, item.id, next))
                }
              />
            )}

            {list.canWrite && (
              <button
                type="button"
                aria-label={`Take ${item.text} off ${list.name}`}
                title={`Take ${item.text} off ${list.name}`}
                onClick={() => onRun(() => packingApi.removeItem(unitId, list.id, item.id))}
                className="inline-flex items-center justify-center"
                style={{
                  minHeight: 'var(--tap-min)',
                  minWidth: 'var(--tap-min)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                }}
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {list.canWrite && (
        <form
          className="flex flex-wrap items-end gap-2"
          style={{ marginTop: 'var(--space-3)' }}
          onSubmit={async (e) => {
            e.preventDefault();

            // See the note on the Save button above: not disabled, so nothing to explain.
            if (!adding.trim()) {
              addField.current?.focus();
              return;
            }

            const added = await onRun(() => packingApi.addItem(unitId, list.id, { text: adding }));
            if (added) setAdding('');
          }}
        >
          <div style={{ flex: '1 1 12rem' }}>
            <TextField
              ref={addField}
              label="Add something"
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              placeholder="Sleeping bag"
            />
          </div>
          <Button variant="secondary" type="submit">
            Add
          </Button>
        </form>
      )}
    </div>
  );
}
